/**
 * Service pro agregaci dat pro Gantt Timeline
 * Transformuje směny a odstávky do struktury vhodné pro zobrazení v gantt diagramu
 */

import { ShiftModel, PlanItemModel, WorkplaceModel } from '@/libs/Api';
import {
    GanttTimelineData,
    GanttTimelineFilters,
    WorkplaceRow,
    TimeSegment,
    GanttEvent,
    SegmentType,
    EventType,
} from '@/libs/tpm-analytics/core/gantt-timeline.types';
import { GanttTimelineRepository } from '@/libs/tpm-analytics/data/gantt-timeline.repository';

/**
 * Pomocná struktura pro interval
 */
interface Interval {
    from: Date;
    to: Date | null; // null = probíhající
}

/**
 * Vrací konec intervalu s podporou probíhajících odstávek
 * Pokud je to null, použije aktuální čas
 */
function getIntervalEnd(to: Date | string | null): Date {
    if (to === null || to === undefined) {
        return new Date();
    }
    return typeof to === 'string' ? new Date(to) : to;
}

/**
 * Detekce překryvu dvou intervalů
 * Používá logiku: x.from < y.to AND x.to > y.from
 * S podporou neukončených intervalů (to = null)
 */
function intervalsOverlap(x: Interval, y: Interval): boolean {
    const xTo = getIntervalEnd(x.to);
    const yTo = getIntervalEnd(y.to);
    return x.from < yTo && xTo > y.from;
}

/**
 * Sjednocení překrývajících se intervalů pomocí sweep-line algoritmu
 * Vrací pole nesjednocených (merged) intervalů
 */
function mergeIntervals(intervals: Interval[]): Interval[] {
    if (intervals.length === 0) return [];
    
    // Seřadíme podle začátku
    const sorted = [...intervals].sort((a, b) => a.from.getTime() - b.from.getTime());
    
    const result: Interval[] = [];
    let current = { ...sorted[0] };
    
    for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        const currentEnd = getIntervalEnd(current.to);
        
        // Překrývají se nebo navazují?
        if (next.from <= currentEnd) {
            // Sloučit - vzít pozdější konec
            const nextEnd = getIntervalEnd(next.to);
            if (nextEnd > currentEnd) {
                current.to = next.to; // Může být null pokud next je probíhající
            }
        } else {
            // Nepřekrývají se - uložit current a začít nový
            result.push(current);
            current = { ...next };
        }
    }
    
    result.push(current);
    return result;
}

/**
 * Vypočítá běhové segmenty (směny minus odstávky)
 * Vrací segmenty kdy stroj běžel
 */
function calculateRunningSegments(
    shift: Interval,
    downtimes: Interval[]
): Interval[] {
    const shiftEnd = getIntervalEnd(shift.to);
    
    // Merge všech odstávek
    const mergedDowntimes = mergeIntervals(downtimes);
    
    // Najdeme volné segmenty mezi odstávkami
    const segments: Interval[] = [];
    let currentStart = shift.from;
    
    for (const downtime of mergedDowntimes) {
        const downtimeEnd = getIntervalEnd(downtime.to);
        
        // Pokud odstávka začíná po aktuálním startu, máme volný segment
        if (downtime.from > currentStart) {
            segments.push({
                from: currentStart,
                to: downtime.from,
            });
        }
        
        // Posuneme start za konec odstávky
        if (downtimeEnd > currentStart) {
            currentStart = downtimeEnd;
        }
    }
    
    // Přidat zbývající segment od poslední odstávky do konce směny
    if (currentStart < shiftEnd) {
        segments.push({
            from: currentStart,
            to: shift.to, // Zachováme původní to (může být null)
        });
    }
    
    return segments;
}

/**
 * Hlavní service pro načtení a agregaci dat
 */
export class GanttTimelineService {
    /**
     * Načte a zpracuje všechna data pro gantt timeline
     */
    static async getGanttTimelineData(
        fromIso: string,
        toIso: string,
        filters?: GanttTimelineFilters
    ): Promise<GanttTimelineData> {
        // Načtení dat z repository
        const { shifts, plannedDowntimes, unplannedDowntimes } = 
            await GanttTimelineRepository.fetchAllData(fromIso, toIso, filters);

        // Seskupení dat po pracovištích
        const workplaceMap = new Map<string, {
            workplace: WorkplaceModel;
            shifts: ShiftModel[];
            plannedDowntimes: PlanItemModel[];
            unplannedDowntimes: PlanItemModel[];
        }>();

        // Zpracování směn
        for (const shift of shifts) {
            const wpId = shift.workplaceId;
            if (!wpId || !shift.workplace) continue;
            
            if (!workplaceMap.has(wpId)) {
                workplaceMap.set(wpId, {
                    workplace: shift.workplace,
                    shifts: [],
                    plannedDowntimes: [],
                    unplannedDowntimes: [],
                });
            }
            workplaceMap.get(wpId)!.shifts.push(shift);
        }

        // Zpracování plánovaných odstávek
        for (const downtime of plannedDowntimes) {
            const wpId = downtime.workplaceId;
            if (!wpId) continue;
            
            if (!workplaceMap.has(wpId)) {
                if (!downtime.workplace) continue;
                workplaceMap.set(wpId, {
                    workplace: downtime.workplace,
                    shifts: [],
                    plannedDowntimes: [],
                    unplannedDowntimes: [],
                });
            }
            workplaceMap.get(wpId)!.plannedDowntimes.push(downtime);
        }

        // Zpracování neplánovaných odstávek
        for (const downtime of unplannedDowntimes) {
            const wpId = downtime.workplaceId;
            if (!wpId) continue;
            
            if (!workplaceMap.has(wpId)) {
                if (!downtime.workplace) continue;
                workplaceMap.set(wpId, {
                    workplace: downtime.workplace,
                    shifts: [],
                    plannedDowntimes: [],
                    unplannedDowntimes: [],
                });
            }
            workplaceMap.get(wpId)!.unplannedDowntimes.push(downtime);
        }

        // Vytvoření řádků pro každé pracoviště
        const workplaceRows: WorkplaceRow[] = [];

        for (const [wpId, data] of workplaceMap.entries()) {
            const row = this.createWorkplaceRow(wpId, data, new Date(fromIso), new Date(toIso));
            workplaceRows.push(row);
        }

        // Seřadit podle názvu pracoviště
        workplaceRows.sort((a, b) => {
            const nameA = a.workplaceName || '';
            const nameB = b.workplaceName || '';
            return nameA.localeCompare(nameB, 'cs', { sensitivity: 'base' });
        });

        return {
            workplaces: workplaceRows,
            timeRange: {
                from: new Date(fromIso),
                to: new Date(toIso),
            },
        };
    }

    /**
     * Vytvoří řádek gantt diagramu pro jedno pracoviště
     */
    private static createWorkplaceRow(
        workplaceId: string,
        data: {
            workplace: WorkplaceModel;
            shifts: ShiftModel[];
            plannedDowntimes: PlanItemModel[];
            unplannedDowntimes: PlanItemModel[];
        },
        rangeFrom: Date,
        rangeTo: Date
    ): WorkplaceRow {
        const segments: TimeSegment[] = [];
        const events: GanttEvent[] = [];

        // Pro každou směnu vypočítáme segmenty
        for (const shift of data.shifts) {
            if (!shift.fromDate || !shift.toDate) continue;
            
            const shiftInterval: Interval = {
                from: new Date(shift.fromDate),
                to: new Date(shift.toDate),
            };

            // Najdeme všechny odstávky překrývající se se směnou
            const shiftPlannedDowntimes: Interval[] = [];
            const shiftUnplannedDowntimes: Interval[] = [];

            for (const pd of data.plannedDowntimes) {
                if (!pd.shutdownFromDate) continue;
                const interval: Interval = {
                    from: new Date(pd.shutdownFromDate),
                    to: pd.shutdownToDate ? new Date(pd.shutdownToDate) : null,
                };
                if (intervalsOverlap(shiftInterval, interval)) {
                    shiftPlannedDowntimes.push(interval);
                }
            }

            for (const ud of data.unplannedDowntimes) {
                if (!ud.shutdownFromDate) continue;
                const interval: Interval = {
                    from: new Date(ud.shutdownFromDate),
                    to: ud.shutdownToDate ? new Date(ud.shutdownToDate) : null,
                };
                if (intervalsOverlap(shiftInterval, interval)) {
                    shiftUnplannedDowntimes.push(interval);
                }
            }

            // Vypočítáme běhové segmenty (směna minus všechny odstávky)
            const allDowntimes = [...shiftPlannedDowntimes, ...shiftUnplannedDowntimes];
            const runningIntervals = calculateRunningSegments(shiftInterval, allDowntimes);

            // Přidáme běhové segmenty
            for (const interval of runningIntervals) {
                segments.push({
                    from: interval.from,
                    to: interval.to,
                    type: 'running',
                    isOngoing: interval.to === null,
                });
            }

            // Přidáme plánované odstávky jako segmenty
            const mergedPlanned = mergeIntervals(shiftPlannedDowntimes);
            for (const interval of mergedPlanned) {
                segments.push({
                    from: interval.from,
                    to: interval.to,
                    type: 'planned-downtime',
                    isOngoing: interval.to === null,
                });
            }

            // Přidáme neplánované odstávky jako segmenty
            const mergedUnplanned = mergeIntervals(shiftUnplannedDowntimes);
            for (const interval of mergedUnplanned) {
                segments.push({
                    from: interval.from,
                    to: interval.to,
                    type: 'unplanned-downtime',
                    isOngoing: interval.to === null,
                });
            }
        }

        // Vytvoříme události z odstávek
        for (const pd of data.plannedDowntimes) {
            if (!pd.shutdownFromDate || !pd.id) continue;
            events.push({
                id: pd.id,
                from: new Date(pd.shutdownFromDate),
                to: pd.shutdownToDate ? new Date(pd.shutdownToDate) : null,
                type: 'planned',
                planItem: pd,
                workplaceId,
                isOngoing: !pd.shutdownToDate,
            });
        }

        for (const ud of data.unplannedDowntimes) {
            if (!ud.shutdownFromDate || !ud.id) continue;
            events.push({
                id: ud.id,
                from: new Date(ud.shutdownFromDate),
                to: ud.shutdownToDate ? new Date(ud.shutdownToDate) : null,
                type: 'unplanned',
                planItem: ud,
                workplaceId,
                isOngoing: !ud.shutdownToDate,
            });
        }

        // Seřadit segmenty podle času
        segments.sort((a, b) => a.from.getTime() - b.from.getTime());
        events.sort((a, b) => a.from.getTime() - b.from.getTime());

        return {
            workplaceId,
            workplaceName: data.workplace.name || '',
            workplaceCode: data.workplace.code || null,
            locationName: data.workplace.location?.name || null,
            segments,
            events,
        };
    }
}

